/**
 * CSV utilities for expense import/export.
 * Parse CSV files client-side, validate, and produce the JSON payload
 * accepted by POST /api/expenses/import.
 */

import Papa from 'papaparse';

export interface CsvExpenseRow {
  Description: string;
  Amount: string;
  Date: string;
  Category: string;
  Vendor?: string;
  Reference?: string;
  IsRecurring?: string;
  RecurrenceFrequency?: string;
  PaymentDueDay?: string;
  IsTaxDeductible?: string;
  TaxCategory?: string;
  BusinessProfile?: string;
  Notes?: string;
}

export interface ParsedExpense {
  categoryId: string;
  businessProfileId: string | null;
  description: string;
  amount: number;
  expenseDate: string;
  vendor: string | null;
  referenceNumber: string | null;
  isRecurring: boolean;
  recurrenceFrequency: string | null;
  isTaxDeductible: boolean;
  taxCategory: string | null;
  paymentDueDay: number | null;
  notes: string | null;
}

export interface ParseError {
  row: number;
  field: string;
  message: string;
}

export interface ParseResult {
  valid: ParsedExpense[];
  invalid: Array<{ row: number; raw: CsvExpenseRow; errors: string[] }>;
}

export interface CategoryLookup {
  id: string;
  name: string;
  slug: string;
}

export interface BusinessProfileLookup {
  id: string;
  name: string;
}

const CSV_HEADERS = [
  'Description',
  'Amount',
  'Date',
  'Category',
  'Vendor',
  'Reference',
  'IsRecurring',
  'RecurrenceFrequency',
  'PaymentDueDay',
  'IsTaxDeductible',
  'TaxCategory',
  'BusinessProfile',
  'Notes',
];

const RECURRENCE_FREQUENCIES = new Set(['weekly', 'monthly', 'quarterly', 'annual']);

function parseBool(val: string | undefined): boolean {
  if (!val) return false;
  const v = val.trim().toLowerCase();
  return v === 'true' || v === 'yes' || v === 'y' || v === '1' || v === 'x';
}

function parseDate(val: string | undefined): { value: string | null; error?: string } {
  if (!val || !val.trim()) return { value: null };
  const trimmed = val.trim();
  // Accept YYYY-MM-DD, MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/;
  if (iso.test(trimmed)) return { value: trimmed };
  const match = trimmed.match(us);
  if (match) {
    const month = parseInt(match[1]).toString().padStart(2, '0');
    const day = parseInt(match[2]).toString().padStart(2, '0');
    let year = match[3];
    if (year.length === 2) year = '20' + year;
    return { value: `${year}-${month}-${day}` };
  }
  return { value: null, error: `Invalid date "${val}" — use YYYY-MM-DD or MM/DD/YYYY` };
}

function parseAmount(val: string | undefined): { value: number | null; error?: string } {
  if (!val || !val.trim()) return { value: null, error: 'Amount is required' };
  // Strip $, commas, whitespace
  const cleaned = val.replace(/[$,\s]/g, '');
  const num = parseFloat(cleaned);
  if (isNaN(num)) return { value: null, error: `Invalid amount "${val}"` };
  if (num <= 0) return { value: null, error: 'Amount must be greater than zero' };
  return { value: num };
}

function parseDueDay(val: string | undefined): { value: number | null; error?: string } {
  if (!val || !val.trim()) return { value: null };
  const num = parseInt(val.trim());
  if (isNaN(num)) return { value: null, error: `Invalid due day "${val}"` };
  if (num < 1 || num > 31) return { value: null, error: 'Due day must be 1-31' };
  return { value: num };
}

function findCategory(name: string | undefined, categories: CategoryLookup[]): { id: string | null; error?: string } {
  if (!name || !name.trim()) return { id: null, error: 'Category is required' };
  const search = name.trim().toLowerCase();
  const match = categories.find(
    (c) => c.name.toLowerCase() === search || c.slug.toLowerCase() === search
  );
  if (!match) {
    return { id: null, error: `Unknown category "${name}". Valid categories: ${categories.map(c => c.name).join(', ')}` };
  }
  return { id: match.id };
}

function findBusinessProfile(name: string | undefined, profiles: BusinessProfileLookup[]): { id: string | null; error?: string } {
  if (!name || !name.trim()) return { id: null };
  const search = name.trim().toLowerCase();
  const match = profiles.find((p) => p.name.toLowerCase() === search);
  if (!match) {
    return { id: null, error: `Unknown business profile "${name}"` };
  }
  return { id: match.id };
}

/**
 * Parse a CSV file and validate rows against the available categories/profiles.
 * Returns valid (ready to insert) and invalid (needs correction) rows.
 */
export async function parseCsvFile(
  file: File,
  categories: CategoryLookup[],
  profiles: BusinessProfileLookup[]
): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    Papa.parse<CsvExpenseRow>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (results) => {
        const valid: ParsedExpense[] = [];
        const invalid: ParseResult['invalid'] = [];

        results.data.forEach((row, idx) => {
          const rowNum = idx + 2; // +2 because headers are row 1 and idx is 0-based
          const errors: string[] = [];

          // Description
          const description = (row.Description || '').trim();
          if (!description) errors.push('Description is required');

          // Amount
          const amt = parseAmount(row.Amount);
          if (amt.error) errors.push(amt.error);

          // Date
          const date = parseDate(row.Date);
          if (date.error) errors.push(date.error);

          // Category
          const cat = findCategory(row.Category, categories);
          if (cat.error) errors.push(cat.error);

          // Business profile (optional)
          const profile = findBusinessProfile(row.BusinessProfile, profiles);
          if (profile.error) errors.push(profile.error);

          // Recurrence frequency
          const freq = (row.RecurrenceFrequency || '').trim().toLowerCase();
          if (freq && !RECURRENCE_FREQUENCIES.has(freq)) {
            errors.push(`Invalid recurrence "${row.RecurrenceFrequency}" — use weekly, monthly, quarterly, or annual`);
          }

          // Due day
          const dueDay = parseDueDay(row.PaymentDueDay);
          if (dueDay.error) errors.push(dueDay.error);

          if (errors.length > 0) {
            invalid.push({ row: rowNum, raw: row, errors });
            return;
          }

          valid.push({
            categoryId: cat.id!,
            businessProfileId: profile.id,
            description,
            amount: amt.value!,
            expenseDate: date.value || new Date().toISOString().split('T')[0],
            vendor: (row.Vendor || '').trim() || null,
            referenceNumber: (row.Reference || '').trim() || null,
            isRecurring: parseBool(row.IsRecurring),
            recurrenceFrequency: freq || null,
            isTaxDeductible: parseBool(row.IsTaxDeductible),
            taxCategory: (row.TaxCategory || '').trim() || null,
            paymentDueDay: dueDay.value,
            notes: (row.Notes || '').trim() || null,
          });
        });

        resolve({ valid, invalid });
      },
      error: (err) => reject(err),
    });
  });
}

/**
 * Export given expenses as CSV string for download.
 */
export function expensesToCsv(
  expenses: Array<{
    description: string;
    amount: number;
    expenseDate: string;
    categoryName: string;
    vendor: string | null;
    referenceNumber: string | null;
    isRecurring: boolean;
    recurrenceFrequency: string | null;
    paymentDueDay: number | null;
    isTaxDeductible: boolean;
    taxCategory: string | null;
    businessProfileName: string | null;
    notes: string | null;
  }>
): string {
  const rows = expenses.map((e) => ({
    Description: e.description,
    Amount: e.amount.toFixed(2),
    Date: e.expenseDate ? e.expenseDate.split('T')[0] : '',
    Category: e.categoryName,
    Vendor: e.vendor || '',
    Reference: e.referenceNumber || '',
    IsRecurring: e.isRecurring ? 'yes' : '',
    RecurrenceFrequency: e.recurrenceFrequency || '',
    PaymentDueDay: e.paymentDueDay?.toString() || '',
    IsTaxDeductible: e.isTaxDeductible ? 'yes' : '',
    TaxCategory: e.taxCategory || '',
    BusinessProfile: e.businessProfileName || '',
    Notes: e.notes || '',
  }));
  return Papa.unparse(rows, { columns: CSV_HEADERS });
}

/**
 * Generate a downloadable CSV template with headers + one example row.
 */
export function generateCsvTemplate(sampleCategoryName: string = 'Utilities'): string {
  const example = {
    Description: 'Electric bill - Main Office',
    Amount: '245.80',
    Date: '2026-04-15',
    Category: sampleCategoryName,
    Vendor: 'Duke Energy',
    Reference: 'INV-12345',
    IsRecurring: 'yes',
    RecurrenceFrequency: 'monthly',
    PaymentDueDay: '15',
    IsTaxDeductible: 'yes',
    TaxCategory: 'Utilities',
    BusinessProfile: '',
    Notes: 'Office electricity',
  };
  return Papa.unparse([example], { columns: CSV_HEADERS });
}

/**
 * Trigger a file download in the browser.
 */
export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
